import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { exportToExcel, importFromExcel } from '../services/api';
import { Role } from '@redpanda/types';
import { Button, Label, LoadingButton } from '@redpanda/ui';

const DataIOPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const userRole = userContext?.user?.role;
  const navigate = useNavigate();

  // Export states
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [exportProgress, setExportProgress] = useState(0);
  const exportableSheets = ['accounts', 'journals', 'invoices'];

  // Import states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingServer, setIsProcessingServer] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userRole !== Role.SUPER_ADMIN && userRole !== Role.ADMIN) {
      navigate('/');
    }
  }, [userRole, navigate]);

  const handleSheetSelection = (sheet: string) => {
    setSelectedSheets(prev =>
      prev.includes(sheet) ? prev.filter(s => s !== sheet) : [...prev, sheet]
    );
  };

  const handleSelectAll = () => {
    setSelectedSheets(exportableSheets);
  };

  const handleDeselectAll = () => {
    setSelectedSheets([]);
  };

  const handleExportExcel = async () => {
    if (selectedSheets.length === 0) {
      alert(t('dataIO:export.selectSheetsError'));
      return;
    }
    setIsExcelExporting(true);
    setExportProgress(0);

    // 進度模擬
    const progressInterval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);

    try {
      const response = await exportToExcel(selectedSheets);
      setExportProgress(100);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'export.xlsx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename\*?=UTF-8''(.+)/);
        if (fileNameMatch?.[1]) {
          fileName = decodeURIComponent(fileNameMatch[1]);
        } else {
          const regularFileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if(regularFileNameMatch?.[1]) fileName = regularFileNameMatch[1];
        }
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      alert(t('dataIO:export.success'));
    } catch (error) {
      console.error('Failed to export to Excel:', error);
      alert(t('dataIO:export.failed'));
    } finally {
      clearInterval(progressInterval);
      setIsExcelExporting(false);
      setTimeout(() => setExportProgress(0), 1000);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith('.xlsx')) {
      setSelectedFile(file);
      setImportResult(null);
    } else {
      setSelectedFile(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert(t('dataIO:import.selectFileError'));
      return;
    }
    setIsImporting(true);
    setUploadProgress(0);
    setIsProcessingServer(false);
    setImportResult(null);
    setImportProgress(0);

    // 進度模擬
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 8;
      });
    }, 400);

    try {
      const onUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        if (percentCompleted === 100) {
          setIsProcessingServer(true);
        }
      };

      const response = await importFromExcel(selectedFile, onUploadProgress);
      setImportProgress(100);
      setImportResult({
        ...response.data,
        success: true
      });

    } catch (error: any) {
      console.error('Failed to import file:', error);
      const errorMessage = error.response?.data?.message || t('dataIO:import.failed');
      setImportResult({
        message: 'import_excel_error',
        error: errorMessage,
        report: error.response?.data?.report || {}
      });
    } finally {
      clearInterval(progressInterval);
      setIsImporting(false);
      setIsProcessingServer(false);
      setSelectedFile(null);
      setUploadProgress(0);
      setTimeout(() => setImportProgress(0), 1000);
      if(fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (userRole !== Role.SUPER_ADMIN && userRole !== Role.ADMIN) return null;

  return (
    <div className="container mx-auto px-10 py-6 pb-32 md:p-8 pb-16">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">{t('dataIO:title')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Export Card */}
        <div className="bg-white dark:bg-slate-800 shadow-md rounded-lg p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('dataIO:export.title')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('dataIO:export.description')}</p>
            <div className="space-y-2 mb-4">
              {exportableSheets.map(sheet => (
                <Label key={sheet} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSheets.includes(sheet)}
                    onChange={() => handleSheetSelection(sheet)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>{t(`translation:components.dataIO.sheets.${sheet}`)}</span>
                </Label>
              ))}
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Button size="sm" onClick={handleSelectAll}>
                {t('dataIO:export.selectAll')}
              </Button>
              <Button size="sm" variant="secondary" onClick={handleDeselectAll}>
                {t('dataIO:export.deselectAll')}
              </Button>
            </div>
            <LoadingButton
              type="button"
              onClick={handleExportExcel}
              loading={isExcelExporting}
              disabled={selectedSheets.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
              customText={{
                create: t('dataIO:export.button'),
                createLoading: t('common:status.exporting')
              }}
            />
          </div>

          {/* Export Progress Bar */}
          {isExcelExporting && exportProgress > 0 && (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-slate-700">
                <div
                  className="bg-teal-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${Math.round(exportProgress)}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('dataIO:export.progress', { percent: Math.round(exportProgress) })}
              </div>
            </div>
          )}
        </div>

        {/* Import Card */}
        <div className="bg-white dark:bg-slate-800 shadow-md rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('dataIO:import.title')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('dataIO:import.description')}</p>
          <div className="flex items-stretch space-x-4">
            <Label
              className="inline-flex items-center justify-center px-4 rounded-md shadow-sm tracking-wide uppercase border transition-all duration-200 bg-white dark:bg-slate-700 text-gray-500 dark:text-gray-300 border-gray-300 dark:border-slate-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-600 h-10 text-sm font-bold"
            >
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4 4-4-4h3V7h2v4z" />
              </svg>
              <span className="truncate">{selectedFile ? selectedFile.name : t('dataIO:import.selectFile')}</span>
              <input
                ref={fileInputRef}
                type='file'
                className="hidden"
                accept=".xlsx"
                onChange={handleFileChange}
              />
            </Label>
            <LoadingButton
              type="button"
              onClick={handleImport}
              loading={isImporting}
              disabled={!selectedFile}
              className="whitespace-nowrap bg-green-600 hover:bg-green-700"
              customText={{
                create: t('dataIO:import.button'),
                createLoading: t('common:status.importing')
              }}
            />
          </div>
          {/* Import Progress Bar */}
          {isImporting && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {uploadProgress < 100 ? t('dataIO:import.uploadProgress', { percent: uploadProgress }) :
                 isProcessingServer ? t('dataIO:import.serverProcessing') :
                 t('dataIO:import.progressComplete', { percent: Math.round(importProgress) })}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-slate-700 mt-1">
                <div className="bg-green-600 h-2.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${Math.round(importProgress)}%` }}></div>
              </div>
            </div>
          )}
          {importResult && (
            <div className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-slate-700 space-y-4">
              <h4 className="font-semibold text-lg text-gray-800 dark:text-white">
                {t(`dataIO.import.resultTitle.${importResult.message === 'import_excel_success' ? 'success' : 'failed'}`)}
              </h4>
              {importResult.report && Object.keys(importResult.report).length > 0 ? (
                Object.entries(importResult.report).map(([sheetName, result]: [string, any]) => (
                  <div key={sheetName} className="p-3 bg-white dark:bg-gray-600 rounded-md shadow-sm">
                    <p className="font-bold text-md text-gray-700 dark:text-white">{sheetName}</p>
                    <div className="flex justify-around text-center mt-2">
                      <div className="text-green-600 dark:text-green-400"><p className="font-bold text-xl">{result.created}</p><p className="text-xs">{t('dataIO:import.stats.created')}</p></div>
                      <div className="text-blue-600 dark:text-blue-400"><p className="font-bold text-xl">{result.updated}</p><p className="text-xs">{t('dataIO:import.stats.updated')}</p></div>
                      <div className="text-red-600 dark:text-red-400"><p className="font-bold text-xl">{result.errors.length}</p><p className="text-xs">{t('dataIO:import.stats.failed')}</p></div>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="mt-2">
                        <details>
                          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">{t('dataIO:import.viewErrors')}</summary>
                          <ul className="list-disc list-inside text-xs text-red-600 dark:text-red-400 max-h-24 overflow-y-auto mt-1">
                            {result.errors.map((err: any, index: number) => (
                              <li key={index}>{t('dataIO:import.errorRow', { row: err.row, message: err.message })}</li>
                            ))}
                          </ul>
                        </details>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">{importResult.error || ''}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataIOPage;
